import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useAtom } from 'jotai';
import axios from 'axios';
import { useHistory, useLocation } from "react-router-dom";
import { IoPeopleCircleSharp } from "react-icons/io5";
import { MdOutlineDynamicFeed, MdFavorite } from "react-icons/md";
import TextToHTML from '../../Components/TextToHtml';
import LikeButton from '../../Components/LikeButton';
import FluidMediaModal from '../../Components/feedmediamodal';
import config from '../../Config';
import DarkModeContext from '../../Components/DarkModeContext';
import { isAuthAtom, authUserAtom, feedListAtom, feedTabAtom, feedNextPageUrlAtom, feedScrollYAtom } from '../../Services/store/atoms';
import ScrollToTopButton from '../../Components/ScrollToTopButton';
import LayoutHomeUser from '../../Layouts/Admin/Layout';
import Spinner from '../../Components/Spinner';

function FeedPublicWithoutHeader(props) {
  const [isAuth] = useAtom(isAuthAtom);
  const [authUser] = useAtom(authUserAtom);
  const { darkMode } = useContext(DarkModeContext);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useAtom(feedListAtom);
  const [nextPageUrl, setNextPageUrl] = useAtom(feedNextPageUrlAtom);
  const [activeTab, setActiveTab] = useAtom(feedTabAtom);
  const [scrollY, setScrollY] = useAtom(feedScrollYAtom);
  const history = useHistory();
  const location = useLocation();
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);
  const [mediaStartIndex, setMediaStartIndex] = useState(0);
  const observerRef = useRef(null);
  const fetched = useRef(false);

  const apiEndpoint = React.useMemo(() => 
    isAuth
      ? `${config.REACT_APP_BASE_URL}/api/v1/home/FeedPublicAuth`
      : `${config.REACT_APP_BASE_URL}/api/v1/home/FeedPublic`
  , [isAuth]);

  const loadProducts = (pageUrl, append = false) => {
    setIsLoading(true);
    const headers = {};
    if (authUser?.token) {
      headers.Authorization = `Bearer ${authUser.token}`;
    }
    
    const params = { 
      myFavorites: activeTab === 'favorites',
      genre: 'All'
    };

    axios.post(pageUrl || apiEndpoint, params, { headers })
      .then((response) => {
        const newProducts = response.data.data.map(product => ({
          ...product,
          liked: false,
          likeCount: product.likes_count || 0
        }));

        setProducts(prevProducts => {
          if (append) {
            const uniqueProducts = newProducts.filter(newProduct =>
              !prevProducts.some(prevProduct => prevProduct.id === newProduct.id)
            );
            return [...prevProducts, ...uniqueProducts];
          }
          return newProducts;
        });

        setNextPageUrl(response.data.next_page_url);
        setIsLoading(false);
      })
      .catch(function (error) {
        console.error(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (fetched.current) {
      setProducts([]);
      loadProducts(apiEndpoint);
      return;
    }
    if (products.length === 0) {
      loadProducts(apiEndpoint);
    } else if (scrollY > 0) {
      window.scrollTo(0, scrollY);
      setScrollY(0);
    }
    fetched.current = true;

    return () => setScrollY(window.scrollY);
  }, []);

  const lastProductRef = useCallback((node) => {
    if (isLoading || !nextPageUrl) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        loadProducts(nextPageUrl, true);
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [isLoading, nextPageUrl]);

  const onCardClick = (userId) => {
    setScrollY(window.scrollY);
    const origin = location.state?.background || location;
    const base = origin.pathname.split('/')[1];
    history.push(`/${base}/profileModal/${userId}`, { background: origin });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setProducts([]);
    loadProducts(apiEndpoint);
  };

  const handleMediaClick = (mediaUrls, index = 0) => {
    setSelectedMediaUrls(mediaUrls);
    setMediaStartIndex(index);
    setShowMediaModal(true);
  };

  return (
    <LayoutHomeUser>
      <div className="ap-screen ap-screen-inset -m-2 lg:-m-4 p-2 lg:p-4 pb-10 min-h-[calc(100dvh-3.5rem)]">
      <div className="flex flex-col items-center max-w-2xl mx-auto px-2 pt-4 pb-2">
        {isAuth ? (
          <div className="ap-segmented w-full max-w-xs mb-2" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'all'}
              className="ap-segment"
              onClick={() => handleTabChange('all')}
            >
              <IoPeopleCircleSharp className='text-base' />
              <span>All</span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'favorites'}
              className="ap-segment"
              onClick={() => handleTabChange('favorites')}
            >
              <MdFavorite className='text-base' />
              <span>Favorites</span>
            </button>
          </div>
        ) : null}
      </div>

      {products.length > 0 ? (
        <div className="flex flex-col justify-center items-center w-full">
          {products.map((val, key) => {
            const isLast = products.length === key + 1;
            return (
              <div
                key={key}
                ref={isLast ? lastProductRef : null}
                className="ap-card relative mt-4 p-4 max-w-2xl w-full mx-2"
              >
                <div className="flex items-center justify-between mb-2">
                  <a onClick={() => onCardClick(val.user_id)} className="flex items-center cursor-pointer min-w-0">
                    <span className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{ boxShadow: '0 0 0 0.5px var(--ap-separator)' }}>
                      <img src={val.avatar_thumb_url || val.avatar} alt="User Avatar" className="object-cover w-full h-full" />
                    </span>
                    <span className="ap-headline capitalize ml-3 truncate">{val.user_date?.nickname}</span>
                  </a>
                  <LikeButton feedId={val.id} />
                </div>
                <div className="ap-body px-1 py-1">
                  <TextToHTML text={val.description} />
                </div>
                <div className={`mt-3 w-full overflow-x-auto ${val.files?.length > 1 ? 'grid grid-cols-2 gap-2' : 'flex'}`}>
                  {val.files?.map((file, index) => (
                    <img
                      key={index}
                      src={file.url}
                      alt={`File ${index}`}
                      className={`w-full object-cover cursor-pointer rounded-[14px] ${(val.files?.length || 0) > 1 ? 'aspect-square' : ''}`}
                      onClick={() => handleMediaClick(val.files.map(f => f.url), index)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : !isLoading ? (
        <div className="flex flex-col items-center mt-16">
          <MdOutlineDynamicFeed className="text-5xl" style={{ color: 'var(--ap-label-3)' }} />
          <p className="ap-subhead mt-4">No hay contenido para mostrar</p>
        </div>
      ) : null}
      </div>

      {isLoading && <Spinner />}
      <ScrollToTopButton />

      {showMediaModal && (
        <FluidMediaModal
          show={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          mediaUrls={selectedMediaUrls}
          startIndex={mediaStartIndex}
        />
      )}
    </LayoutHomeUser>
  );
}

export default FeedPublicWithoutHeader;